import React, { Fragment, useCallback, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    CheckboxInput,
    ButtonSecondaryCompact,
    TextLoader,
    UI_BODY_SEMIBOLD,
    UI_BODY_SEMIBOLD_SM,
    colors2,
    useScreenWidth,
    useScroll,
    DateRangePicker,
    ButtonGhostV2,
    TextInput,
    Divider,
    UI_BODY,
    makeArrayFromLength,
    useHandleClickOutside
} from "oolib";
import NumberRangeSlider from "@/components/inputs/NumberRangeSlider";
import { FilterActionsButton } from "../../../../discovery/FilterModule/comps/FilterActionsButton";
import {
    StyledActionsArea,
    StyledFiltersWrapper,
    StyledLightBoxActionButton,
    StyledOptionsLightboxWrapper,
    StyledOptionsLoaderWrapper,
    StyledOptionWrapper,
    StyledSelectClearButtonsWrapper,
    StyledSelectClearInnerWrapper,
} from "../../styled";
import { useDebounce } from "@/utils/customHooks";
import { isLocalSearchFilterType, useSearchMethod } from "./utils/useSearchMethod";
import { DynamicOptionsLightboxShell } from "./DynamicOptionsLightboxShell";

const LightboxBreakpointPX = 700;



export const FilterOptionsLoader = ({ isMobile }) => {
  return( <StyledOptionsLoaderWrapper>
              <TextLoader style={{ width: isMobile ? "2.4rem" : "1.9rem", height: isMobile ? "2.4rem" : "1.8rem", borderRadius: "4px" }}/> 
              <TextLoader style={{ width: "70%", height: isMobile ? "2.4rem" : "1.8rem" }} />
    </StyledOptionsLoaderWrapper>
  )
}

const MultiSelectFilterTypes = ({ id, options, onChange, value, searchTerm, observerRef, isFetchingNextPage, isMobile }) => {

  const handleOptionClick = (option) => {
    if(option.loading) return

    let newValue = [...value];
    let isSelected = newValue.some((d) => d.value === option.value);
    if (!isSelected) {
      newValue.push(option);
    } else {
      newValue = newValue.filter((d) => d.value !== option.value);
    }

    onChange(id, newValue);
};

  

  return (
    <Fragment>
      {options?.length ? options?.map((d, index) => (
          <StyledOptionWrapper
            key={d.value}
            onClick={() => handleOptionClick(d)}
            ref={observerRef}
          >
            {d.loading ? (
              <FilterOptionsLoader />
            ) : (
                <CheckboxInput className={'filterOptionsLightbox__checkboxInput'} option={d} value={value} />
            )}
          </StyledOptionWrapper>
        )) : 
          (<div style={{ padding: "1rem" }}>
            <UI_BODY>{ searchTerm ? `No results for ${searchTerm}` : `No filters`}</UI_BODY>
          </div>)
        } 
        {isFetchingNextPage && 
            makeArrayFromLength(10).map((_, i) => 
              <FilterOptionsLoader key={`loader-${i}`} isMobile={isMobile} />
            )
        }
    </Fragment>
  )
}

export const DateRangeType = ({id, onChange, value}) => {
    const [val, setVal] = useState(value);

    const handleChange = (k, v) => {
      setVal(v)
      if(Array.isArray(v) && v.length === 2 && v.every(vv => !!vv === true)){
        onChange(id, v)
      }
    }

  return (
    <DateRangePicker
      onChange={handleChange}
      value={val}
      id={id}
      />
  )
}

export const NumberRangeType = ({ id, onChange, value, min, max, step = 1, loading, aggregatedData, showModeToggle = true }) => {
  // aggregatedData contains the bucketed data from aggrMetricsAcrossDimensions
  // Structure: { data: [{ value, display, count, ... }], metadata: { dimensions: { [path]: { min, max } } } }
  // This will be used for bar graph visualization in the slider component

  const [localValue, setLocalValue] = useState(value);
  const debounce = useDebounce({ interval: 400 });

  // Sync local value when external value changes (e.g., filter cleared)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
        <TextLoader style={{ width: '100%', height: '4rem' }} />
      </div>
    );
  }

  const handleChange = (_, v) => {
    setLocalValue(v); // Immediate UI update
    debounce(() => onChange(id, v)); // Debounced API call
  };

  return (
    <div style={{ padding: '3px 12px 0' }}>
      <NumberRangeSlider
        id={id}
        onChange={handleChange}
        value={localValue}
        min={min ?? 0}
        max={max ?? 100}
        step={step}
        showInputs={true}
        showLabels={true}
        showModeToggle={showModeToggle}
        aggregatedData={aggregatedData}
      />
    </div>
  );
}

const LightboxComp = ({id, comp, props, onChange, value, searchTerm, observerRef, isFetchingNextPage, isMobile }) => {

  const compsLookup = {
    DateRangeType,
    MultiSelectFilterTypes,
    NumberRangeType
  }

  const Comp = compsLookup[comp]

  return (
    <Comp
      id={id}
      onChange={onChange}
      value={value}
      searchTerm={searchTerm}
      observerRef={observerRef}
      isFetchingNextPage={isFetchingNextPage}
      isMobile={isMobile}
      {...props}
      />
  )
}


export const OptionsLightbox = ({

    onClickOutside,
    onClose,
    ref_anchorPoint,

    id,
    value,
    onChange,
    lightboxConfig,
    title,
    onSearchChange,
    observerRef,
    filterConfig,
    isFetchingNextPage,
    hasNextPage,
  }) => {

  const screenWidth = useScreenWidth();
  const actionsAreaRef = useRef();
  const debounce = useDebounce();

  const {
    search,
    filteredOptions,
    handleSearchChange,
    resetSearch,
  } = useSearchMethod({filterConfig, lightboxConfig, onSearchChange, debounce}); // searches locally on frontend or on backend based on filterConfig

  const handleClickOutside = useCallback(() => {
    resetSearch();
    onClickOutside && onClickOutside();
  }, [resetSearch, onClickOutside]);

  const handleClose = useCallback(() => {
    resetSearch();
    onClose && onClose();
  }, [resetSearch, onClose]);

  const isMobile = screenWidth < LightboxBreakpointPX;

  // These filter types don't need search and have special width/overflow
  const isRangeFilterType = ['DateRangeType', 'NumberRangeType'].includes(lightboxConfig.comp);

  const optionsLightboxWidth = isMobile ? 300 : isRangeFilterType ? 300 : 250;

  const lightboxProps = {...lightboxConfig.props};

  if ((isLocalSearchFilterType(filterConfig)) && filteredOptions !== null) {
    lightboxProps.options = filteredOptions;
  }

  return <DynamicOptionsLightboxShell
            { ...{
              overflow: isRangeFilterType ? 'visible' : undefined,
              width: isRangeFilterType ? { desktop: 300, mobile: 300 } : undefined,
              isMobile,
              ref_anchorPoint,
              onClickOutside: handleClickOutside,
              onClose: handleClose
            }}
            >

      {/* Actions area, Search, sort, clear, select all */}
      <StyledActionsArea ref={actionsAreaRef}>
          {/* Title area mobile */}
              {isMobile &&
                <div style={{borderBottom: isRangeFilterType ? `1px solid ${colors2.grey10}` : null ,}}>
                  <div style={{ padding: '1.2rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                    <UI_BODY_SEMIBOLD style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      whiteSpace: 'normal',
                      lineHeight: '1.2',
                      minWidth: 0,
                      flex: 1
                    }}>{title}</UI_BODY_SEMIBOLD>
                    <ButtonSecondaryCompact onClick={handleClose} value={"Close"}/>
                  </div>
                </div>
          }
            {!isMobile && 
            <div style={{ padding: "0.9rem 1rem" }}>
            <UI_BODY_SEMIBOLD_SM
              style={{
                color: colors2.grey80,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal',
                lineHeight: '1.2',
              }}
            >
              {title}
            </UI_BODY_SEMIBOLD_SM>
            </div>
            }
            {isRangeFilterType ? null : <TextInput 
              value={search}
              onChange={(id, v) => handleSearchChange(v)}
              placeholder={"Search"}
              icon="MagnifyingGlass"
              />}
            {lightboxConfig?.comp === 'MultiSelectFilterTypes' && <StyledSelectClearButtonsWrapper>
              <StyledSelectClearInnerWrapper>
                {/* <StyledLightBoxActionButton
                  disabled={ hasNextPage || lightboxConfig.props?.options?.some(i => i.loading) || lightboxConfig.props?.options?.length === value.length}
                  icon="CheckCircle"
                  iconSize={16}
                  iconWeight="bold"
                  iconColor={colors2.grey80}
                  onClick={() => onChange(id, lightboxConfig.props?.options)}
                  responsive={true}
                  isMobile={isMobile}
                >
                  Select All
                </StyledLightBoxActionButton> */}
                <StyledLightBoxActionButton
                  disabled={value.length === 0}
                  icon="XCircle"
                  iconSize={16}
                  iconWeight="bold"
                  iconColor={colors2.grey80}
                  onClick={() => onChange(id, [])}
                  responsive={true}
                  isMobile={isMobile}
                >
                  Clear All
                </StyledLightBoxActionButton>
              </StyledSelectClearInnerWrapper>
            </StyledSelectClearButtonsWrapper>}
          </StyledActionsArea>


      {/* Filter Options area */}
          <StyledFiltersWrapper 
            lightboxConfig={lightboxConfig}
            >
            <LightboxComp
              id={id}
              onChange={onChange}
              value={value}
              comp={lightboxConfig.comp}
              props={lightboxProps}
              searchTerm={search}
              observerRef={observerRef}
              isFetchingNextPage={isFetchingNextPage}
              isMobile={isMobile}
              />
          </StyledFiltersWrapper>

    </DynamicOptionsLightboxShell>;
};